// src/synchronizers/SynchronizerSpot.js
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3007';

export class SynchronizerSpot {
  constructor(coordinator) {
    this.coordinator = coordinator;
    this.spotElement = this.createSpotElement();
    this.queue = [];
    this.isPlaying = false;
    this.currentSpot = null;

    coordinator?.registerModule('spot', this);
    console.log('[SyncSpot] ✅ Inicializado');
  }

  createSpotElement() {
    let element = document.getElementById('spot-audio-element');
    if (!element) {
      element = document.createElement('audio');
      element.id = 'spot-audio-element';
      element.style.display = 'none';
      element.preload = 'auto';
      document.body.appendChild(element);
    }
    return element;
  }

  // 1) SPOT CHEGA → ENTRA NA FILA
  addToQueue(spot) {
    if (!spot) return false;

    // evita duplicata se tiver id
    if (spot.id && this.queue.some((s) => s.id === spot.id)) {
      console.warn('[SyncSpot] ⚠️ Spot já na fila:', spot.id);
      return false;
    }

    const normalized = {
      ...spot,
      id: spot.id || `${spot.nome || 'spot'}-${Date.now()}`,
      addedAt: Date.now(),
    };

    this.queue.push(normalized);

    console.log(
      `[SyncSpot] ➕ Adicionado: ${normalized.nome || normalized.id} (fila: ${this.queue.length})`,
    );

    if (!this.isPlaying) {
      this.processQueue();
    }

    return true;
  }

  async processQueue() {
    if (this.isPlaying || this.queue.length === 0) return;

    const nextSpot = this.queue.shift();
    this.currentSpot = nextSpot;

    try {
      await this.executeSpot(nextSpot);
    } catch (err) {
      console.error('[SyncSpot] ❌ Erro ao executar spot:', err);
    } finally {
      this.currentSpot = null;
    }

    if (this.queue.length > 0) {
      setTimeout(() => this.processQueue(), 300);
    }
  }

  // 2–6) POSICIONAMENTO ENTRE MÚSICAS
  async executeSpot(spot) {
  this.isPlaying = true;
  const blockId = `spot-${spot.id || Date.now()}`;

  // ✅ Bloquear ANTES de qualquer await (corrige a race)
  this.coordinator?.blockAdvance(blockId);

  try {
    // Agora é safe preparar o áudio: se a música acabar aqui,
    // o ended já vê a playlist bloqueada.
    const audioUrl = await this.prepareAudioUrl(spot);
    if (!audioUrl) {
      console.warn('[SyncSpot] ⚠️ URL do áudio do spot não disponível, abortando.');
      return;
    }

    // Se ainda tem música tocando, deixa terminar
    if (this.isMusicPlaying()) {
      await this.waitForMusicEnd();
    }

    // Tocar spot SEM ducking (music off / entre faixas)
    this.spotElement.src = audioUrl;
    this.spotElement.volume = 1.0;

    // Retry básico pra AbortError
    try {
      await this.spotElement.play();
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('[SyncSpot] ⚠️ Play abortado - retry.');
        await new Promise((r) => setTimeout(r, 100));
        await this.spotElement.play();
      } else {
        throw error;
      }
    }

    // Espera o spot terminar (com timeout de segurança)
    await this.waitForSpotEnd();
  } catch (err) {
    console.error('[SyncSpot] ❌ Falha em executeSpot:', err);
  } finally {
    this.isPlaying = false;

    // ✅ SEMPRE desbloquear, mesmo em erro
    this.coordinator?.unblockAdvance(blockId);

    // Limpar elemento de spot
    try {
      this.spotElement.pause();
      this.spotElement.src = '';
      this.spotElement.currentTime = 0;
    } catch {
      // ignore
    }
  }
}

  // --------- resolução de URL de áudio ---------

  async prepareAudioUrl(spot) {
    // 1: audioUrl
    if (spot.audioUrl) {
      if (/^https?:\/\//.test(spot.audioUrl)) {
        return spot.audioUrl;
      }
      return `${API_BASE}${spot.audioUrl}`;
    }

    // 2: arquivo enviado
    if (spot.audioFile) {
      const fileName = spot.audioFile.split('/').pop();
      return `${API_BASE}/uploads/${fileName}`;
    }

    // 3: TTS se tiver texto
    if (spot.texto || spot.text) {
      return this.generateTTS(spot.texto || spot.text, spot.tipo);
    }

    return null;
  }

  async generateTTS(texto, tipo = 'comercial') {
    console.log('[SyncSpot] 🎙️ Gerando TTS...');
    try {
      const response = await fetch(`${API_BASE}/api/generate-audio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: texto, tipo }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`TTS falhou: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      if (!data.success || !data.audioUrl) {
        throw new Error('Resposta TTS inválida');
      }

      return `${API_BASE}${data.audioUrl}`;
    } catch (error) {
      console.error('[SyncSpot] ❌ Erro ao gerar TTS:', error);
      return null;
    }
  }

  // --------- interação com a música ---------

  isMusicPlaying() {
    const audio = this.coordinator?.audioManager?.audioElement;
    return !!(audio && !audio.paused && audio.currentTime > 0);
  }

  async waitForMusicEnd() {
  const audio = this.coordinator?.audioManager?.audioElement;
  if (!audio || audio.paused) return;

  return new Promise((resolve) => {
    let remaining = 0;

    if (Number.isFinite(audio.duration) && audio.duration > 0) {
      remaining = Math.max(
        0,
        (audio.duration || 0) - (audio.currentTime || 0),
      );
    } else {
      // fallback se não tiver duration válido (stream, bug, etc.)
      remaining = 120; // 2 min só como segurança
    }

    const SAFETY_SECONDS = 2;
    const MAX_WAIT_SECONDS = 30 * 60; // 30 minutos de teto pra não travar nunca
    const waitSeconds = Math.min(remaining + SAFETY_SECONDS, MAX_WAIT_SECONDS);
    const timeoutMs = waitSeconds * 1000;

    console.log(
      `[SyncSpot] ⏳ Aguardando fim da música (~${remaining.toFixed(
        1,
      )}s, timeout ${waitSeconds.toFixed(1)}s)`,
    );

    const onEnd = () => {
      cleanup();
      resolve();
    };

    const onTimeout = () => {
      console.log('[SyncSpot] ⏱️ Timeout da música - seguindo mesmo assim.');
      cleanup();
      resolve();
    };

    const cleanup = () => {
      clearTimeout(timeout);
      audio.removeEventListener('ended', onEnd);
    };

    const timeout = setTimeout(onTimeout, timeoutMs);
    audio.addEventListener('ended', onEnd, { once: true });
  });
}

  async waitForSpotEnd() {
    return new Promise((resolve) => {
      const duration = this.spotElement.duration || 60;
      const timeoutMs = Math.max((duration + 5) * 1000, 10000);

      const onEnd = () => {
        cleanup();
        resolve();
      };

      const onError = (e) => {
        console.error('[SyncSpot] ❌ Erro no áudio de spot:', e);
        cleanup();
        resolve();
      };

      const onTimeout = () => {
        console.log('[SyncSpot] ⏱️ Timeout do spot - finalizando.');
        cleanup();
        resolve();
      };

      const cleanup = () => {
        clearTimeout(timeout);
        this.spotElement.removeEventListener('ended', onEnd);
        this.spotElement.removeEventListener('error', onError);
      };

      const timeout = setTimeout(onTimeout, timeoutMs);
      this.spotElement.addEventListener('ended', onEnd, { once: true });
      this.spotElement.addEventListener('error', onError, { once: true });
    });
  }

  // --------- utilitários / debug ---------

  removeFromQueue(spotId) {
    const idx = this.queue.findIndex((s) => s.id === spotId);
    if (idx === -1) return false;
    const removed = this.queue.splice(idx, 1)[0];
    console.log('[SyncSpot] ➖ Removido da fila:', removed.nome || removed.id);
    return true;
  }

  clearQueue() {
    const count = this.queue.length;
    this.queue = [];
    console.log(`[SyncSpot] 🧹 Fila limpa (${count} spots removidos)`);
  }

  getStatus() {
    return {
      queue: [...this.queue],
      isPlaying: this.isPlaying,
      currentSpot: this.currentSpot
        ? { id: this.currentSpot.id, nome: this.currentSpot.nome }
        : null,
    };
  }

  destroy() {
    console.log('[SyncSpot] 🧹 Destruindo...');

    if (this.isPlaying) {
      this.spotElement.pause();
    }

    this.clearQueue();

    if (this.spotElement && this.spotElement.parentNode) {
      this.spotElement.parentNode.removeChild(this.spotElement);
    }

    if (this.coordinator) {
      this.coordinator.unregisterModule?.('spot');
    }

    console.log('[SyncSpot] ✅ Destruído');
  }
}

export default SynchronizerSpot;
